import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { OrgStatus } from 'constants/enums';
import { logoutWithDelay } from 'store/oauth/service';
import { fetchInterventions } from 'store/tasks/service';

import { initialState } from './initialState';
import {
  fetchGroups,
  fetchTagSVGs,
  fetchTagLabels,
  fetchTimeLogTasks,
  fetchTimeLogTypes,
  fetchGroupAdmins,
  fetchQuestionnaires,
  fetchGroupCarePrograms,
  fetchGroupAvatar,
  fetchObservationCollections,
  fetchNotifyActivities,
  updateNotifyActivity,
  fetchDeviceOrderings
} from './service';

const slice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    setSelectedGid: (state, action: PayloadAction<string>) => {
      state.selectedGid = action.payload;
      state.lastSelectedGid = action.payload;
    },
    setSelectedPatients: (state, action: PayloadAction<any[]>) => {
      state.selectedPatients = action.payload;
    },
    updateGroup: (state, action: PayloadAction<{ group: IGroupInfo; propagate: boolean }>) => {
      state.groupInfo.data =
        state.groupInfo.data.map((item) => {
          if (item.gid === action.payload.group.gid) {
            return { ...item, ...action.payload.group };
          }
          if (action.payload.propagate && item.parentGid === action.payload.group.gid) {
            return { ...item, ...action.payload.group, gid: item.gid };
          } else {
            return item;
          }
        }) || null;
    },
    updateTagLabel: (
      state,
      action: PayloadAction<{ gid: string; tagId: string; tagLabel: string }>
    ) => {
      state.groupTagLabels = state.groupTagLabels.map((group) => {
        if (group.gid === action.payload.gid) {
          return {
            ...group,
            tags: group.tags?.map((tag) => {
              if (tag.tagId === action.payload.tagId) {
                tag.tagLabel = action.payload.tagLabel;
              }
              return tag;
            })
          };
        }
        return group;
      });
    },
    updateAllTags: (state, action: PayloadAction<{ gid: string; tags: ITag[] }>) => {
      const tagsWithLabels = action.payload.tags.filter(
        (tag) => tag.tagLabel !== null && tag.tagLabel !== ''
      );
      const newGroupTags: IGroupTagLabels[] = [];
      if (state.groupTagLabels.length === 0) {
        newGroupTags.push({
          gid: action.payload.gid,
          tags: tagsWithLabels
        });
      } else {
        newGroupTags.push(
          ...state.groupTagLabels.map((group) => {
            if (group.gid === action.payload.gid) {
              return {
                gid: group.gid,
                tags: tagsWithLabels
              };
            }
            return group;
          })
        );
      }
      state.groupTagLabels = newGroupTags;
    },
    addQuestionnaire: (
      state,
      action: PayloadAction<{
        gid: string;
        surveyId: string;
        title: string;
        scoreId?: string;
        scoreTitle?: string;
      }>
    ) => {
      state.questionnaires.loading = true;
      const newQuestionnaire: ISurvey = {
        survey_type: 'questionnaire',
        survey_id: action.payload.surveyId,
        title: action.payload.title,
        sub_title: null,
        pages: [],
        order_: '0',
        max_value: '0',
        server_modified_date: Date.now().toString(),
        deleted: '0',
        archived: '0',
        project: 'tactio',
        read_only: '0',
        status: 0
      };
      if (action.payload.scoreId) {
        newQuestionnaire.score = {
          scoreId: action.payload.scoreId,
          surveyId: action.payload.surveyId,
          title: '' // unknown
        };
      }
      state.questionnaires.data = [...state.questionnaires.data, newQuestionnaire];
      state.questionnaires.loading = false;
    },
    archiveQuestionnaire: (state, action: PayloadAction<{ gid: string; surveyId: string }>) => {
      state.questionnaires.loading = true;
      state.questionnaires.data = state.questionnaires.data.filter(
        (questionnaire) => questionnaire.survey_id !== action.payload.surveyId
      );
      state.questionnaires.loading = false;
    },
    setLastOpenedUid: (state, action: PayloadAction<string>) => {
      state.lastOpenedUid = action.payload;
    },
    clearAvatar: (state) => {
      state.groupAvatar = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.groupInfo.loading = true;
        state.groupInfo.error = false;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        if (action.payload.length > 1) {
          const allMessagingDisabled = action.payload?.every(
            (group) => group.patientEngageChannelEnabled === 0
          );
          const patientEngageChannelEnabled: 0 | 1 = allMessagingDisabled ? 0 : 1;
          const allGroups = {
            gid: 'all',
            name: 'allGroupsKey',
            parentGid: 'all',
            orgName: 'all',
            groupCountry: 'USA',
            patientEngageChannelEnabled,
            orgStatus: OrgStatus.ACTIVE
          };

          state.groupInfo.data = [
            allGroups,
            ...action.payload.sort((a, b) => {
              // all groups always on top
              if (a.gid === 'all') return -1;
              else if (a.name && b.name) {
                if (
                  // ensure that a group with a name before 'all' is sorted after
                  b.gid !== 'all' &&
                  a.name?.toLowerCase() < b.name?.toLowerCase()
                )
                  return -1;
                else if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
              }
              return 0;
            })
          ];
        } else {
          state.groupInfo.data = action.payload;
        }

        const hasAccessToSelected = state.groupInfo.data.some(
          (group) => group.gid === state.lastSelectedGid
        );

        let gidToSelect = state.lastSelectedGid || 'all';
        if (!hasAccessToSelected || !state.lastSelectedGid) {
          const orgs = state.groupInfo.data.filter(
            (group) => group.gid !== 'all' && group.parentGid === group.gid
          );
          const firstOrg = orgs.length > 0 ? orgs[0] : null;
          const firstGroup = state.groupInfo.data.find(
            (group) => group.gid !== 'all' && group.parentGid !== group.gid
          );
          if (orgs.length >= 2) {
            // user has access to 2+ orgs => support user. Defaults to "all" to see all data
            gidToSelect = 'all';
          } else if (firstOrg) {
            // user has access to 1+ org => regular org user. Defaults to that org
            gidToSelect = firstOrg.gid;
          } else if (firstGroup) {
            // user has access to NO orgs => regular group-only user. Defaults to 1st org
            gidToSelect = firstGroup?.gid;
          }
        }
        state.lastSelectedGid = gidToSelect;
        state.selectedGid = gidToSelect;

        state.groupInfo.loading = false;
      })
      .addCase(fetchGroups.rejected, (state) => {
        state.groupInfo.loading = false;
        state.groupInfo.error = true;
      })
      .addCase(fetchTagSVGs.fulfilled, (state, action) => {
        state.groupTagSvgs = action.payload;
      })
      .addCase(fetchTagLabels.fulfilled, (state, action) => {
        state.groupTagLabels = action.payload;
      })
      .addCase(fetchGroupAdmins.pending, (state) => {
        state.groupAdmins.loading = true;
      })
      .addCase(fetchGroupAdmins.fulfilled, (state, action) => {
        state.groupAdmins.data = action.payload;
        state.groupAdmins.loading = false;
      })
      .addCase(fetchGroupAdmins.rejected, (state) => {
        state.groupAdmins.loading = false;
      })
      .addCase(fetchTimeLogTasks.fulfilled, (state, action) => {
        state.timeLogTasks = action.payload;
      })
      .addCase(fetchTimeLogTypes.fulfilled, (state, action) => {
        state.timeLogTypes = action.payload;
      })
      .addCase(fetchInterventions.fulfilled, (state, action) => {
        state.interventions = action.payload;
      })
      .addCase(fetchQuestionnaires.pending, (state) => {
        state.questionnaires.loading = true;
        state.questionnaires.error = false;
      })
      .addCase(fetchQuestionnaires.fulfilled, (state, action) => {
        state.questionnaires.data = action.payload;
        state.questionnaires.loading = false;
      })
      .addCase(fetchQuestionnaires.rejected, (state) => {
        state.questionnaires.loading = false;
        state.questionnaires.error = true;
      })
      .addCase(fetchGroupCarePrograms.fulfilled, (state, action) => {
        state.carePrograms = action.payload;
      })
      .addCase(fetchGroupAvatar.fulfilled, (state, action) => {
        state.groupAvatar = action.payload;
      })
      .addCase(fetchObservationCollections.fulfilled, (state, action) => {
        state.observationCollections = action.payload;
      })
      .addCase(fetchDeviceOrderings.fulfilled, (state, action) => {
        state.deviceOrderingsAvailable = action.payload;
      })
      .addCase(fetchNotifyActivities.pending, (state) => {
        state.groupNotifyActivities.loading = true;
        state.groupNotifyActivities.error = false;
      })
      .addCase(fetchNotifyActivities.fulfilled, (state, action) => {
        state.groupNotifyActivities.data = action.payload;
        state.groupNotifyActivities.loading = false;
      })
      .addCase(fetchNotifyActivities.rejected, (state) => {
        state.groupNotifyActivities.loading = false;
        state.groupNotifyActivities.error = true;
      })
      .addCase(updateNotifyActivity.pending, (state) => {
        state.groupNotifyActivities.loading = true;
        state.groupNotifyActivities.error = false;
      })
      .addCase(updateNotifyActivity.fulfilled, (state, action) => {
        if (state.groupNotifyActivities.data) {
          state.groupNotifyActivities.data = action.payload;
        }
        state.groupNotifyActivities.loading = false;
        state.groupNotifyActivities.error = false;
      })
      .addCase(updateNotifyActivity.rejected, (state) => {
        state.groupNotifyActivities.loading = false;
        state.groupNotifyActivities.error = true;
      })
      .addCase(logoutWithDelay.fulfilled, (state) => {
        return {
          ...initialState,
          selectedGid: state.selectedGid,
          lastSelectedGid: state.lastSelectedGid
        };
      });
  }
});

export const {
  setSelectedGid,
  setSelectedPatients,
  updateGroup,
  updateTagLabel,
  updateAllTags,
  addQuestionnaire,
  archiveQuestionnaire,
  setLastOpenedUid,
  clearAvatar
} = slice.actions;

export const groupsReducer = slice.reducer;
